import { useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SendIcon from "@mui/icons-material/Send";

const MAX_LENGTH = 10000;

const SUGGESTION_CHIPS = [
  "Help me prepare for a sales call",
  "What questions should I ask the prospect?",
  "Review my closing technique",
  "How can I handle objections better?",
];

interface ChatInputProps {
  onSend: (content: string) => void;
  disabled?: boolean;
  showSuggestions?: boolean;
}

export function ChatInput({
  onSend,
  disabled = false,
  showSuggestions = false,
}: ChatInputProps) {
  const [value, setValue] = useState("");

  const handleSend = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChipClick = (text: string) => {
    if (disabled) return;
    onSend(text);
  };

  return (
    <Box sx={{ borderTop: 1, borderColor: "divider" }}>
      {/* Suggestion chips */}
      {showSuggestions && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            px: 2,
            pt: 1.5,
          }}
        >
          {SUGGESTION_CHIPS.map((text) => (
            <Chip
              key={text}
              label={text}
              variant="outlined"
              size="small"
              onClick={() => handleChipClick(text)}
              sx={{ cursor: "pointer" }}
            />
          ))}
        </Box>
      )}
      {/* Input row */}
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end", p: 2 }}>
        <TextField
          fullWidth
          multiline
          maxRows={6}
          size="medium"
          placeholder="Ask your sales coach for advice..."
          value={value}
          onChange={(e) => setValue(e.target.value.slice(0, MAX_LENGTH))}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          slotProps={{
            input: {
              sx: { fontSize: "0.95rem" },
            },
          }}
        />
        <IconButton
          onClick={handleSend}
          disabled={disabled || !value.trim()}
          sx={{
            mb: 0.5,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            "&:hover": { bgcolor: "primary.dark" },
            "&.Mui-disabled": { bgcolor: "grey.300", color: "grey.500" },
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>
      {value.length > MAX_LENGTH * 0.9 && (
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            px: 2,
            pb: 1
          }}>
          {value.length.toLocaleString()} / {MAX_LENGTH.toLocaleString()}
        </Typography>
      )}
    </Box>
  );
}
